/* eslint-disable @typescript-eslint/naming-convention */
import { Ng1StateDeclaration, StateProvider, UrlRouterProvider, StateService } from "@uirouter/angularjs";
import core from "./core/module";
import { StateFactory } from "./stateFactory";
import { EffectivePermissionsService } from "./permissions/effectivePermissionsService";
import { MessagingService } from "./sockets/messagingService";
import { AccountResolverService } from "./state/accountResolverService";
import * as whiteboardStates from "@gtmhub/whiteboards/whiteboards.states";
import * as employeesStates from "@gtmhub/employees/employees.states";
import * as teamsStates from "@gtmhub/teams/teams.states";
import * as pluginStates from "@gtmhub/plugins/plugins.states";
import insightStates, { insightboardsStateName, insightboardStateName } from "./insights/insights.states";
import { CurrentUserRolesActions, RolesActions } from "@gtmhub/roles";
import { INgRedux } from "@gtmhub/state-management";
import { FeatureToggleService } from "./featureToggle";
import { async } from "@gtmhub/core/routing";
import { IGtmhubRootScopeService } from "./models";
import { ExpandNavV2Service } from "./navigation/expand-nav-v2.service";
import { IQService } from "angular";
import { AccountService } from "./accounts/accounts-service";

function registerStates($stateProvider: StateProvider, $urlRouterProvider: UrlRouterProvider): void {
  const register = (states: Ng1StateDeclaration[]): void => {
    for (const state of states) {
      $stateProvider.state(state);
    }
  };

  const stateFactory = new StateFactory();

  // root state that requires a token, profile, effectivePermissions and available features (editions)
  // see accountResolverService authResolverService
  const rootProtectedState = stateFactory.rootProtectedState();
  rootProtectedState.resolve = {
    // all sub-states are going to resolve the returned promise which will make the effective permissions available in the $rootScope
    effectivePermissions: [
      "EffectivePermissionsService",
      function (effectivePermissionsService: EffectivePermissionsService) {
        return effectivePermissionsService.initEffectivePermissions();
      },
    ],
    _account: [
      "AccountResolverService",
      function (accountResolverService: AccountResolverService) {
        return accountResolverService.initAccount();
      },
    ],
    webSocketMessaging: [
      "MessagingService",
      function (messagingService: MessagingService) {
        messagingService.init();
      },
    ],
    currentUserRoles: [
      "$ngRedux",
      "CurrentUserRolesActions",
      function ($ngRedux: INgRedux, currentUserRolesActions: CurrentUserRolesActions) {
        return $ngRedux.dispatch(currentUserRolesActions.getCurrentUserRoles());
      },
    ],
    roles: [
      "$ngRedux",
      "RolesActions",
      function ($ngRedux: INgRedux, rolesActions: RolesActions) {
        return $ngRedux.dispatch(rolesActions.getRolesIfMissing());
      },
    ],
    newExperience: [
      "$rootScope",
      "ExpandNavV2Service",
      "$q",
      "AccountService",
      "FeatureToggleService",
      function (
        $rootScope: IGtmhubRootScopeService,
        expandNavV2Service: ExpandNavV2Service,
        $q: IQService,
        accountService: AccountService,
        featureToggleService: FeatureToggleService
      ) {
        $q.all({
          newExperienceEnabled: featureToggleService.isFeatureAvailable("new-experience"),
          newExperienceToggleEnabled: featureToggleService.isFeatureAvailable("new-experience-toggle"),
          optionEnabled: accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience"),
        }).then(({ newExperienceEnabled, newExperienceToggleEnabled, optionEnabled }) => {
          const newExperienceEnabledForUser = newExperienceEnabled && newExperienceToggleEnabled && optionEnabled?.enabled;

          $rootScope.newExperienceEnabledForUser = newExperienceEnabledForUser;
          $rootScope.hideMenuV2 = newExperienceEnabledForUser && !expandNavV2Service.getCurrentValue();

          expandNavV2Service.subscribe(({ value }) => {
            $rootScope.hideMenuV2 = newExperienceEnabledForUser && !value;
          });
        });
      },
    ],
  };

  // We want to create a performance mark once all dependencies
  // for the root state have been resolved
  rootProtectedState.onEnter = [
    ...Object.keys(rootProtectedState.resolve),
    () => {
      performance.mark("Root State Enter");
    },
  ];

  const ___________taskState = (state: string) => {
    $stateProvider.state(state, stateFactory.taskStates().task());
  };

  const ______editDynamicMetricStates = (state: string) => {
    $stateProvider.state(state, stateFactory.goalStates().editDynamicMetric());
  };

  const ______editManualMetricStates = (state: string) => {
    $stateProvider.state(state, stateFactory.goalStates().editMetric());
  };

  const ______treeStates = (state: string) => {
    $stateProvider.state(state, stateFactory.goalStates().alignmentTree());

    $stateProvider.state(`${state}._addSlice`, stateFactory.goalStates()._addSlice());
    $stateProvider.state(`${state}._addSlice.addSlice`, stateFactory.goalStates().addSlice());

    ___goalSidebarStates(state);

    ______goalFormStates(state);
  };

  const _____employeesStates = (state: string) => {
    $stateProvider.state(`${state}`, stateFactory.employeeStates().employees());

    $stateProvider.state(`${state}._badge`, stateFactory.badgeStates()._badgeAssignment());
    $stateProvider.state(`${state}._badge.badge`, stateFactory.badgeStates().badgeAssignment());

    $stateProvider.state(`${state}._manage`, stateFactory.teamStates()._manage());
    $stateProvider.state(`${state}._manage.manage`, stateFactory.teamStates().manage());
  };

  const ______employeeStates = (state: string, config?: Ng1StateDeclaration) => {
    config && $stateProvider.state({ ...config, name: `${state}${config.name}` });

    $stateProvider.state(`${state}.employee._badge`, stateFactory.badgeStates()._badgeAssignment());
    $stateProvider.state(`${state}.employee._badge.badge`, stateFactory.badgeStates().badgeAssignment());

    // Manage access --> user details sidebar
    ______userDetailView(`${state}.employee`);
    changeUserAvatarView(`${state}.employee`);
    _____editUserDetails(`${state}.employee`);

    $stateProvider.state(`${state}.employee._metric`, stateFactory.goalStates()._metric());
    $stateProvider.state(`${state}.employee._metric.metric`, stateFactory.goalStates().metric());
    $stateProvider.state(`${state}.employee._metric.metric._updateMetric`, stateFactory.goalStates()._updateMetric());
    $stateProvider.state(`${state}.employee._metric.metric._updateMetric.update`, stateFactory.goalStates().updateMetricForm());
    $stateProvider.state(`${state}.employee._metric.metric._updateMetric.edit`, stateFactory.goalStates().editMetricForm());

    $stateProvider.state(`${state}.employee._metric.metric._task`, stateFactory.taskStates()._task());
    ___________taskState(`${state}.employee._metric.metric._task.task`);

    $stateProvider.state(`${state}.employee._metric.metric._message`, stateFactory.inboxStates()._message());
    $stateProvider.state(`${state}.employee._metric.metric._message.thread`, stateFactory.inboxStates().thread());

    // edit dynamic metric
    $stateProvider.state(`${state}.employee._metric.metric._editMetric`, stateFactory.goalStates()._editMetric());
    ______editDynamicMetricStates(`${state}.employee._metric.metric._editMetric.editDynamicMetric`);

    // edit manual metric
    ______editManualMetricStates(`${state}.employee._metric.metric._editMetric.editMetric`);
    ___goalSidebarStates(`${state}.employee`);
  };

  const ___goalSidebarStates = (state: string) => {
    $stateProvider.state(`${state}._goalSidebar`, stateFactory.goalStates()._singleGoal());
    $stateProvider.state(`${state}._goalSidebar.goal`, stateFactory.goalStates().goalDetails());

    $stateProvider.state(`${state}._goalSidebar.goal._marketplace`, stateFactory.goalStates()._marketplace());
    $stateProvider.state(`${state}._goalSidebar.goal._marketplace.submit`, stateFactory.goalStates().submit());

    $stateProvider.state(`${state}._goalSidebar.goal._timeline`, stateFactory.goalStates()._timeline());
    $stateProvider.state(`${state}._goalSidebar.goal._timeline.timeline`, stateFactory.goalStates().timeline());

    // metric preview
    $stateProvider.state(`${state}._goalSidebar.goal._metric`, stateFactory.goalStates()._metric());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric`, stateFactory.goalStates().metric());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._updateMetric`, stateFactory.goalStates()._updateMetric());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._updateMetric.update`, stateFactory.goalStates().updateMetricForm());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._updateMetric.edit`, stateFactory.goalStates().editMetricForm());

    //
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._message`, stateFactory.inboxStates()._message());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._message.thread`, stateFactory.inboxStates().thread());
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._task`, stateFactory.taskStates()._task());
    ___________taskState(`${state}._goalSidebar.goal._metric.metric._task.task`);

    // edit dynamic metric
    $stateProvider.state(`${state}._goalSidebar.goal._metric.metric._editMetric`, stateFactory.goalStates()._editMetric());
    ______editDynamicMetricStates(`${state}._goalSidebar.goal._metric.metric._editMetric.editDynamicMetric`);

    // edit manual metric
    ______editManualMetricStates(`${state}._goalSidebar.goal._metric.metric._editMetric.editMetric`);

    // update metric
    $stateProvider.state(`${state}._goalSidebar.goal._updateMetric`, stateFactory.goalStates()._updateMetric());
    $stateProvider.state(`${state}._goalSidebar.goal._updateMetric.update`, stateFactory.goalStates().updateMetricForm());

    // create metric
    $stateProvider.state(`${state}._goalSidebar.goal._createMetric`, stateFactory.goalStates()._createMetric());
    $stateProvider.state(`${state}._goalSidebar.goal._createMetric.createMetric`, stateFactory.goalStates().createMetricForm());
    // create dynamic metric
    $stateProvider.state(`${state}._goalSidebar.goal._createMetric.createDynamicMetric`, stateFactory.goalStates().createDynamicMetric());

    // edit manual metric
    $stateProvider.state(`${state}._goalSidebar.goal._editMetric`, stateFactory.goalStates()._editMetric());
    $stateProvider.state(`${state}._goalSidebar.goal._editMetric.editMetric`, stateFactory.goalStates().editMetric());

    // edit dynamic metric
    $stateProvider.state(`${state}._goalSidebar.goal._editMetric.editDynamicMetric`, stateFactory.goalStates().editDynamicMetric());

    // approve OKR states
    $stateProvider.state(`${state}._goalSidebar.goal._approve`, stateFactory.goalStates()._approve());
    $stateProvider.state(`${state}._goalSidebar.goal._approve.sendForApproval`, stateFactory.goalStates().sendForApproval());
    $stateProvider.state(`${state}._goalSidebar.goal._review`, stateFactory.goalStates()._review());
    $stateProvider.state(`${state}._goalSidebar.goal._review.review`, stateFactory.goalStates().review());

    $stateProvider.state(`${state}._goalSidebar.goal._task`, stateFactory.taskStates()._task());
    ___________taskState(`${state}._goalSidebar.goal._task.task`);

    ______goalFormStates(`${state}._goalSidebar.goal`);
  };

  const ______goalFormStates = (state: string) => {
    $stateProvider.state(`${state}._goalForm`, stateFactory.goalStates()._form());
    $stateProvider.state(`${state}._goalForm.form`, stateFactory.goalStates().goalForm());
  };

  const _________teamsStates = (state: string) => {
    $stateProvider.state(`${state}`, stateFactory.teamStates().teams());

    $stateProvider.state(`${state}._badge`, stateFactory.badgeStates()._badgeAssignment());
    $stateProvider.state(`${state}._badge.badge`, stateFactory.badgeStates().badgeAssignment());

    // create/edit a team
    $stateProvider.state(`${state}._form`, stateFactory.teamStates()._form());
    $stateProvider.state(`${state}._form.form`, stateFactory.teamStates().form());

    // this is the state where you can bulk edit team members from inside the team screen
    $stateProvider.state(`${state}._manage`, stateFactory.teamStates()._manage());
    $stateProvider.state(`${state}._manage.manage`, stateFactory.teamStates().manage());
  };

  const __________teamStates = (state: string, config?: Ng1StateDeclaration) => {
    config && $stateProvider.state({ ...config, name: `${state}${config.name}` });

    $stateProvider.state(`${state}.team._badge`, stateFactory.badgeStates()._badgeAssignment());
    $stateProvider.state(`${state}.team._badge.badge`, stateFactory.badgeStates().badgeAssignment());

    $stateProvider.state(`${state}.team._form`, stateFactory.teamStates()._form());
    $stateProvider.state(`${state}.team._form.form`, stateFactory.teamStates().form());

    $stateProvider.state(`${state}.team._metric`, stateFactory.goalStates()._metric());
    $stateProvider.state(`${state}.team._metric.metric`, stateFactory.goalStates().metric());
    $stateProvider.state(`${state}.team._metric.metric._updateMetric`, stateFactory.goalStates()._updateMetric());
    $stateProvider.state(`${state}.team._metric.metric._updateMetric.update`, stateFactory.goalStates().updateMetricForm());
    $stateProvider.state(`${state}.team._metric.metric._updateMetric.edit`, stateFactory.goalStates().editMetricForm());

    $stateProvider.state(`${state}.team._metric.metric._task`, stateFactory.taskStates()._task());
    ___________taskState(`${state}.team._metric.metric._task.task`);

    $stateProvider.state(`${state}.team._metric.metric._message`, stateFactory.inboxStates()._message());
    $stateProvider.state(`${state}.team._metric.metric._message.thread`, stateFactory.inboxStates().thread());

    // edit dynamic metric
    $stateProvider.state(`${state}.team._metric.metric._editMetric`, stateFactory.goalStates()._editMetric());
    ______editDynamicMetricStates(`${state}.team._metric.metric._editMetric.editDynamicMetric`);

    // edit manual metric
    ______editManualMetricStates(`${state}.team._metric.metric._editMetric.editMetric`);
    ___goalSidebarStates(`${state}.team`);
  };

  const ______userDetailView = (state: string) => {
    $stateProvider.state(`${state}._detail`, stateFactory.accountStates()._userDetailView());
    $stateProvider.state(`${state}._detail.detail`, stateFactory.accountStates().userDetailView());
    // other users avatar change states
    changeUserAvatarView(`${state}._detail.detail`);
    // other users edit name states
    _____editUserDetails(`${state}._detail.detail`);
  };

  const changeUserAvatarView = (state: string) => {
    $stateProvider.state(`${state}._avatar`, stateFactory.accountStates()._avatar());
    $stateProvider.state(`${state}._avatar.changePicture`, stateFactory.accountStates().changeUserAvatar());
  };

  const _____editUserDetails = (state: string) => {
    $stateProvider.state(`${state}._edit`, stateFactory.accountStates()._edit());
    $stateProvider.state(`${state}._edit.editLicense`, stateFactory.accountStates().editUserLicense());
  };

  $stateProvider.state("default", stateFactory.default());
  $stateProvider.state("gtmhub", rootProtectedState);
  $stateProvider.state("error", stateFactory.errorState());

  // ======================================================================================
  // multi-account
  $stateProvider.state("gtmhub.multiAccountInvitationResponse", stateFactory.multiAccountStates().respondToMultiInvitation());

  // ======================================================================================
  // edition plan
  $stateProvider.state("gtmhub.editionPlanChange", stateFactory.configurationStates().editionPlanChange());

  // ======================================================================================
  // account bootstrapStates
  $stateProvider.state("accountBootstrap", stateFactory.accountBootstrapStates().accountBootstrapRoot());
  $stateProvider.state("accountBootstrap.resolveToken", stateFactory.accountBootstrapStates().resolveToken());
  $stateProvider.state("accountBootstrap.resolveAccount", stateFactory.accountBootstrapStates().resolveAccount());
  $stateProvider.state("accountBootstrap.createAccount", stateFactory.accountBootstrapStates().createAccount());
  $stateProvider.state("accountSuspended", stateFactory.accountBootstrapStates().accountSuspendedRoot());
  $stateProvider.state("accountBootstrapModal", stateFactory.accountBootstrapModalStates().accountBootstrapModalRoot());
  $stateProvider.state("accountBootstrapModal.accountLinked", stateFactory.accountBootstrapModalStates().accountLinkedRoot());
  $stateProvider.state("accountBootstrapModal.accountSwitched", stateFactory.accountBootstrapModalStates().accountSwitchedRoot());

  $stateProvider.state("userDeactivated", stateFactory.accountBootstrapStates().userDeactivatedRoot());

  // ======================================================================================
  // people states
  // list of assignees

  // approve badge state
  $stateProvider.state("gtmhub.approveBadges", stateFactory.approveBadgesStates().approve());

  // badge states
  $stateProvider.state("gtmhub.badges", stateFactory.badgeStates().badges());
  $stateProvider.state("gtmhub.badges._form", stateFactory.badgeStates()._form());
  $stateProvider.state("gtmhub.badges._form.form", stateFactory.badgeStates().form("^.^", "^.^"));

  // these 2 states are needed in email "*Someone* wants to award the *X* badge to *someone else*"
  $stateProvider.state("gtmhub.badges._badge", stateFactory.badgeStates()._badgeAssignment());
  $stateProvider.state("gtmhub.badges._badge.badge", stateFactory.badgeStates().badgeAssignment());

  $stateProvider.state("gtmhub.badges._badgeTemplates", stateFactory.badgeStates()._badgeTemplates());
  $stateProvider.state("gtmhub.badges._badgeTemplates.badgeTemplates", stateFactory.badgeStates().badgeTemplates("^.^", "^.^"));

  // Employees states
  _____employeesStates("gtmhub.employees");

  //Team states
  _________teamsStates("gtmhub.teams");

  $stateProvider.state("gtmhub.organization", stateFactory.teamStates().organization());
  $stateProvider.state("gtmhub.organization._manage", stateFactory.teamStates()._manage());
  $stateProvider.state("gtmhub.organization._manage.manage", stateFactory.teamStates().manage());

  $stateProvider.state("gtmhub.organization_chart", stateFactory.teamStates().organizationChart());

  // create/edit a team
  $stateProvider.state("gtmhub.organization._form", stateFactory.teamStates()._form());
  $stateProvider.state("gtmhub.organization._form.form", stateFactory.teamStates().form());

  $stateProvider.state("gtmhub.organization_chart._form", stateFactory.teamStates()._form());
  $stateProvider.state(`gtmhub.organization_chart._form.form`, stateFactory.teamStates().form());

  $stateProvider.state("gtmhub.organization_chart._manage", stateFactory.teamStates()._manage());
  $stateProvider.state("gtmhub.organization_chart._manage.manage", stateFactory.teamStates().manage());

  $stateProvider.state("gtmhub.userNotificationSettings", stateFactory.notificationSettingsStates().userNotificationSettings());

  // ======================================================================================
  // msteams home states
  $stateProvider.state("gtmhub.msTeamsHome", stateFactory.homeStates().msTeamsHome());

  // msTeams home tasks
  $stateProvider.state("gtmhub.msTeamsHome._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.msTeamsHome._task.task");

  // msTeamsHome metric states
  $stateProvider.state("gtmhub.msTeamsHome._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._message.thread", stateFactory.inboxStates().thread());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.msTeamsHome._metric.metric._task.task");

  // edit dynamic metric
  $stateProvider.state("gtmhub.msTeamsHome._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.msTeamsHome._metric.metric._editMetric.editDynamicMetric");

  // edit manual metric
  ______editManualMetricStates("gtmhub.msTeamsHome._metric.metric._editMetric.editMetric");

  $stateProvider.state("gtmhub.msTeamsHome._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.msTeamsHome._updateMetric.update", stateFactory.goalStates().updateMetricForm());

  ___goalSidebarStates("gtmhub.msTeamsHome");
  ______goalFormStates("gtmhub.msTeamsHome");

  $stateProvider.state("gtmhub.msTeamsHome._comment", stateFactory.homeStates()._comment());
  $stateProvider.state("gtmhub.msTeamsHome._comment.thread", stateFactory.homeStates().thread());

  // home states
  $stateProvider.state("gtmhub.home", stateFactory.homeStates().home());

  $stateProvider.state("gtmhub.home._comment", stateFactory.homeStates()._comment());
  $stateProvider.state("gtmhub.home._comment.thread", stateFactory.homeStates().thread());

  // home states

  // temporary redirect states
  $stateProvider.state("gtmhub.home.toHomeMetric", stateFactory.notificationsUrlsRedirectStates().toHomeMetric());
  $stateProvider.state("gtmhub.home.toHomeGoal", stateFactory.notificationsUrlsRedirectStates().toHomeGoal());

  // home welcome-modal
  $stateProvider.state("gtmhub.home._createMetric", stateFactory.goalStates()._createMetric());
  $stateProvider.state("gtmhub.home._createMetric.createMetric", stateFactory.goalStates().createMetricForm());
  $stateProvider.state("gtmhub.home._editMetric", stateFactory.goalStates()._editMetric());
  $stateProvider.state("gtmhub.home._editMetric.editMetric", stateFactory.goalStates().editMetric());

  // home tasks
  $stateProvider.state("gtmhub.home._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.home._task.task");

  // home metric states
  $stateProvider.state("gtmhub.home._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.home._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.home._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.home._metric.metric._message.thread", stateFactory.inboxStates().thread());
  $stateProvider.state("gtmhub.home._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.home._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.home._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());
  $stateProvider.state("gtmhub.home._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.home._metric.metric._task.task");
  $stateProvider.state("gtmhub.home._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.home._metric.metric._editMetric.editDynamicMetric");
  ______editManualMetricStates("gtmhub.home._metric.metric._editMetric.editMetric");
  $stateProvider.state("gtmhub.home._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.home._updateMetric.update", stateFactory.goalStates().updateMetricForm());

  ___goalSidebarStates("gtmhub.home");
  ______goalFormStates("gtmhub.home");

  // global search states
  $stateProvider.state("gtmhub.globalSearch", stateFactory.globalSearchStates().globalSearch());

  // workflow states
  $stateProvider.state("gtmhub.workflow", stateFactory.workflowStates().workflow());

  ___goalSidebarStates("gtmhub.workflow");

  // ======================================================================================

  $stateProvider.state("gtmhub.tasks", stateFactory.taskStates().tasks());

  // temporary redirect states
  $stateProvider.state("gtmhub.toTasksTask", stateFactory.notificationsUrlsRedirectStates().toTasksTask());

  $stateProvider.state("gtmhub.tasks._filter", stateFactory.assigneeStates()._filter());
  $stateProvider.state("gtmhub.tasks._filter.filter", stateFactory.assigneeStates().filter());

  // single tasks view
  ___goalSidebarStates("gtmhub.tasks");

  $stateProvider.state("gtmhub.tasks._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.tasks._task.task");

  $stateProvider.state("gtmhub.tasks._tasks", stateFactory.taskStates()._tasks());
  $stateProvider.state("gtmhub.tasks._tasks.archived", stateFactory.taskStates().archived());

  $stateProvider.state("gtmhub.tasks._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.tasks._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.tasks._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.tasks._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.tasks._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.tasks._metric.metric._message.thread", stateFactory.inboxStates().thread());

  $stateProvider.state("gtmhub.tasks._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.tasks._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.tasks._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());

  $stateProvider.state("gtmhub.tasks._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.tasks._metric.metric._task.task");

  // edit dynamic metric
  $stateProvider.state("gtmhub.tasks._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.tasks._metric.metric._editMetric.editDynamicMetric");

  // edit manual metric
  ______editManualMetricStates("gtmhub.tasks._metric.metric._editMetric.editMetric");

  // ======================================================================================
  // ms-teams-app states
  $stateProvider.state("gtmhub.msteams.tasks._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.msteams.tasks._task.task");

  $stateProvider.state("gtmhub.msteams.tasks._tasks", stateFactory.taskStates()._tasks());
  $stateProvider.state("gtmhub.msteams.tasks._tasks.archived", stateFactory.taskStates().archived());
  $stateProvider.state("gtmhub.msteams.tasks", stateFactory.taskStates().tasks());

  $stateProvider.state("gtmhub.msteams.tasks._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.msteams.tasks._updateMetric.update", stateFactory.goalStates().updateMetricForm());

  $stateProvider.state("gtmhub.msteams.tasks._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._message.thread", stateFactory.inboxStates().thread());

  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());

  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.msteams.tasks._metric.metric._task.task");

  // edit dynamic metric
  $stateProvider.state("gtmhub.msteams.tasks._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.msteams.tasks._metric.metric._editMetric.editDynamicMetric");

  // edit manual metric
  ______editManualMetricStates("gtmhub.msteams.tasks._metric.metric._editMetric.editMetric");

  $stateProvider.state("gtmhub.msteams.tasks._filter", stateFactory.assigneeStates()._filter());
  $stateProvider.state("gtmhub.msteams.tasks._filter.filter", stateFactory.assigneeStates().filter());

  // check whether these states are used at all
  ___goalSidebarStates("gtmhub.msteams.tasks");

  // Goals Grid
  $stateProvider.state("gtmhub.goals", stateFactory.goalStates().goals());
  $stateProvider.state("gtmhub.goals.all", stateFactory.goalStates().allGoals());

  // Edit session
  $stateProvider.state("gtmhub.goals.all.session", stateFactory.planningSessionStates().sessionModal());
  $stateProvider.state("gtmhub.goals.all.session.form", stateFactory.planningSessionStates().sessionForm());

  $stateProvider.state("gtmhub.goals.all.session.form._sessionSelector", stateFactory.planningSessionStates()._sessionSelector());
  $stateProvider.state("gtmhub.goals.all.session.form._sessionSelector.sessionSelector", stateFactory.planningSessionStates().sessionSelector());
  $stateProvider.state("gtmhub.goals.all.session.form._ownerSelector", stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state("gtmhub.goals.all.session.form._ownerSelector.ownerSelector", stateFactory.assigneeStates().ownerSelector());

  $stateProvider.state("gtmhub.goals.all._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.goals.all._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.goals.all._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.goals.all._metric.metric._message.thread", stateFactory.inboxStates().thread());
  $stateProvider.state("gtmhub.goals.all._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.goals.all._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.goals.all._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());

  $stateProvider.state("gtmhub.goals.all._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.goals.all._metric.metric._task.task");

  // edit dynamic metric
  $stateProvider.state("gtmhub.goals.all._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.goals.all._metric.metric._editMetric.editDynamicMetric");

  // edit manual metric
  ______editManualMetricStates("gtmhub.goals.all._metric.metric._editMetric.editMetric");

  ______goalFormStates("gtmhub.goals.all");
  ___goalSidebarStates("gtmhub.goals.all");

  $stateProvider.state("gtmhub.goals.all._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.goals.all._updateMetric.update", stateFactory.goalStates().updateMetricForm());

  // -------------------------------------------------------------
  // ovreview screeen
  $stateProvider.state("gtmhub.goals.overview", stateFactory.planningSessionStates().overview(false));
  $stateProvider.state("gtmhub.goals.overview._inspect", stateFactory.planningSessionStates()._inspect());
  $stateProvider.state("gtmhub.goals.overview._inspect.list", stateFactory.planningSessionStates().inspectList());

  // create goal from the session overview screen
  $stateProvider.state("gtmhub.goals.overview._goalForm", stateFactory.goalStates()._form());
  $stateProvider.state("gtmhub.goals.overview._goalForm.form", stateFactory.goalStates().goalForm());

  // workflow screens
  $stateProvider.state("gtmhub.goals.workflow", stateFactory.planningSessionStates().workflow());
  $stateProvider.state("gtmhub.goals.workflow._inspect", stateFactory.planningSessionStates()._inspect());
  $stateProvider.state("gtmhub.goals.workflow._inspect.goals", stateFactory.planningSessionStates().inspectGoals());
  $stateProvider.state("gtmhub.goals.workflow._inspect.assignees", stateFactory.planningSessionStates().inspectAssignees());
  $stateProvider.state("gtmhub.goals.workflow._inspect.nudge", stateFactory.planningSessionStates().nudge());

  $stateProvider.state("gtmhub.goals.all.bulk", stateFactory.goalStates().bulk());
  $stateProvider.state("gtmhub.goals.all.bulk.assign", stateFactory.goalStates().assign());
  $stateProvider.state("gtmhub.goals.all.bulk.tag", stateFactory.goalStates().tag());

  // sidebar controller should stay above alignmentSidebar.tree state, since it's broadcasting cached data
  // changing the order will block alignment-tree screen
  $stateProvider.state("gtmhub.goals.alignmentSidebar", stateFactory.goalStates().alignmentSidebar());

  ______treeStates("gtmhub.goals.alignmentSidebar.tree");

  $stateProvider.state("gtmhub.goals.alignmentSidebar.nested", stateFactory.goalStates().nested());
  $stateProvider.state(`gtmhub.goals.alignmentSidebar.nested._createMetric`, stateFactory.goalStates()._createMetric());
  $stateProvider.state(`gtmhub.goals.alignmentSidebar.nested._createMetric.createMetric`, stateFactory.goalStates().createMetricForm());
  ___goalSidebarStates("gtmhub.goals.alignmentSidebar.nested");
  ______goalFormStates("gtmhub.goals.alignmentSidebar.nested");

  // ======================================================================================
  // google sites states
  $stateProvider.state("gtmhub.gsites", stateFactory.googleSitesStates().googleSites());
  $stateProvider.state("gtmhub.gsites.goals", stateFactory.goalStates().goals());
  $stateProvider.state("gtmhub.gsites.goals.alignmentSidebar", stateFactory.goalStates().alignmentSidebar());

  ______treeStates("gtmhub.gsites.goals.alignmentSidebar.tree");

  // ======================================================================================
  // sessions states
  // list of sessions
  $stateProvider.state("gtmhub.sessions", stateFactory.planningSessionStates().list());
  $stateProvider.state("gtmhub.sessions._overview", stateFactory.planningSessionStates()._overview());
  $stateProvider.state("gtmhub.sessions._overview.overview", stateFactory.planningSessionStates().overview(true));
  $stateProvider.state("gtmhub.sessions._overview.overview._inspect", stateFactory.planningSessionStates()._inspect());
  $stateProvider.state("gtmhub.sessions._overview.overview._inspect.list", stateFactory.planningSessionStates().inspectList());

  // create/edit goal
  $stateProvider.state("gtmhub.sessions._form", stateFactory.planningSessionStates().sessionModal());
  $stateProvider.state("gtmhub.sessions._form.form", stateFactory.planningSessionStates().sessionForm());

  $stateProvider.state("gtmhub.sessions._form.form._sessionSelector", stateFactory.planningSessionStates()._sessionSelector());
  $stateProvider.state("gtmhub.sessions._form.form._sessionSelector.sessionSelector", stateFactory.planningSessionStates().sessionSelector());
  $stateProvider.state("gtmhub.sessions._form.form._ownerSelector", stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state("gtmhub.sessions._form.form._ownerSelector.ownerSelector", stateFactory.assigneeStates().ownerSelector());

  $stateProvider.state("gtmhub.sessions.form.finish", stateFactory.planningSessionStates().finish()); // this state may not be needed anymore

  // Clone session
  $stateProvider.state("gtmhub.sessions._clone", stateFactory.planningSessionStates()._clone());
  $stateProvider.state("gtmhub.sessions._clone.clone", stateFactory.planningSessionStates().clone());
  $stateProvider.state("gtmhub.sessions._clone.clone.form", stateFactory.planningSessionStates().sessionModal());
  $stateProvider.state("gtmhub.sessions._clone.clone.form.create", stateFactory.planningSessionStates().sessionForm());
  $stateProvider.state("gtmhub.sessions._clone.clone.form.create._sessionSelector", stateFactory.planningSessionStates()._sessionSelector());
  $stateProvider.state("gtmhub.sessions._clone.clone.form.create._sessionSelector.sessionSelector", stateFactory.planningSessionStates().sessionSelector());
  $stateProvider.state("gtmhub.sessions._clone.clone.form.create._ownerSelector", stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state("gtmhub.sessions._clone.clone.form.create._ownerSelector.ownerSelector", stateFactory.assigneeStates().ownerSelector());

  // ======================================================================================
  $stateProvider.state("gtmhub.usersList", stateFactory.accountStates().usersList());
  ______userDetailView("gtmhub.usersList");

  $stateProvider.state("gtmhub.userProfile", stateFactory.accountStates().userProfile());
  $stateProvider.state("gtmhub.userProfile._avatar", stateFactory.accountStates()._avatar());
  $stateProvider.state("gtmhub.userProfile._avatar.upload", stateFactory.accountStates().uploadAvatar());

  // DEPRECATED
  // $stateProvider.state("gtmhub.changePassword", stateFactory.accountStates().changePassword());

  $stateProvider.state("gtmhub.sendChangePasswordRequest", stateFactory.accountStates().sendChangePasswordRequest());
  $stateProvider.state("gtmhub.userLanguage", stateFactory.accountStates().userLanguage());

  // ======================================================================================
  // roles managements
  $stateProvider.state("gtmhub.roles", stateFactory.roleStates().roles());
  $stateProvider.state("gtmhub.roles._createRole", stateFactory.roleStates()._createRole());
  $stateProvider.state("gtmhub.roles._createRole.form", stateFactory.roleStates().createRoleForm());
  $stateProvider.state("gtmhub.roles._createRole.editRole", stateFactory.roleStates().editRole());
  $stateProvider.state("gtmhub.roles._createRole.permissions", stateFactory.roleStates().permissions());
  $stateProvider.state("gtmhub.roles._createRole.done", stateFactory.roleStates().createRoleDone());

  // ======================================================================================
  // reporting states
  $stateProvider.state("gtmhub.reporting", stateFactory.reportingStates().reporting());

  // ======================================================================================
  // kpis states
  $stateProvider.state("gtmhub.kpis", stateFactory.kpiStates().kpis());
  $stateProvider.state("gtmhub.kpis._singleKpi", stateFactory.kpiStates()._singleKpi());
  $stateProvider.state("gtmhub.kpis._singleKpi.new", stateFactory.kpiStates().createNew());
  $stateProvider.state("gtmhub.kpis._singleKpi.form", stateFactory.kpiStates().form());

  // ======================================================================================
  // automation states
  $stateProvider.state("gtmhub.automationList", stateFactory.automationStates().all());
  $stateProvider.state("gtmhub.automationList._automation", stateFactory.automationStates()._automation());
  $stateProvider.state("gtmhub.automationList._automation.form", stateFactory.automationStates().automationForm());

  $stateProvider.state("gtmhub.automation", stateFactory.automationStates().automationSidebar());
  $stateProvider.state("gtmhub.automation.rules", stateFactory.automationStates().rules());
  $stateProvider.state("gtmhub.automation.rules._all", stateFactory.automationStates().automationRules());
  $stateProvider.state("gtmhub.automation.rules._all.trigger", stateFactory.automationStates().triggerRule());
  $stateProvider.state("gtmhub.automation.rules._all.condition", stateFactory.automationStates().conditionRule());
  $stateProvider.state("gtmhub.automation.rules._all.action", stateFactory.automationStates().actionRule());
  $stateProvider.state("gtmhub.automation.history", stateFactory.automationStates().history());

  $stateProvider.state("gtmhub.automationContactSales", stateFactory.automationStates().automationContactSales());

  // ======================================================================================
  // configuration states
  $stateProvider.state("gtmhub.configuration", stateFactory.configurationStates().configuration());
  $stateProvider.state("gtmhub.configuration.methodology", stateFactory.configurationStates().methodologyConfiguration());
  $stateProvider.state("gtmhub.configuration.language", stateFactory.configurationStates().languageConfiguration());
  $stateProvider.state("gtmhub.configuration.reportsAndNotifications", stateFactory.configurationStates().reportsAndNotificationsConfiguration());
  $stateProvider.state(
    "gtmhub.configuration.reportsAndNotifications._accountNotificationSettings",
    stateFactory.notificationSettingsStates()._accountNotificationSettings()
  );
  $stateProvider.state(
    "gtmhub.configuration.reportsAndNotifications._accountNotificationSettings.accountNotificationSettings",
    stateFactory.notificationSettingsStates().accountNotificationSettings()
  );
  $stateProvider.state("gtmhub.configuration.reportsAndNotifications._cadence", stateFactory.configurationStates()._cadence());
  $stateProvider.state("gtmhub.configuration.reportsAndNotifications._cadence.cadence", stateFactory.configurationStates().cadence());
  $stateProvider.state("gtmhub.configuration.methodology._cadence", stateFactory.configurationStates()._cadence());
  $stateProvider.state("gtmhub.configuration.methodology._cadence.cadence", stateFactory.configurationStates().cadence());
  $stateProvider.state("gtmhub.configuration.methodology._cadence.coloring", stateFactory.configurationStates().coloring());
  $stateProvider.state("gtmhub.configuration.methodology._cadence.confidence", stateFactory.configurationStates().confidence());
  $stateProvider.state("gtmhub.configuration.methodology._accountNotificationSettings", stateFactory.notificationSettingsStates()._accountNotificationSettings());
  $stateProvider.state(
    "gtmhub.configuration.methodology._accountNotificationSettings.accountNotificationSettings",
    stateFactory.notificationSettingsStates().accountNotificationSettings()
  );

  // feaures
  $stateProvider.state("gtmhub.configuration.features", stateFactory.configurationStates().featuresConfiguration());

  // slack
  $stateProvider.state("gtmhub.configuration.slack", stateFactory.configurationStates().slackConfiguration());
  $stateProvider.state("gtmhub.configuration.jira", stateFactory.configurationStates().jiraConfiguration());
  // active directory
  // todo: rename active directory to SSO
  $stateProvider.state("gtmhub.configuration.singleSignOn", stateFactory.configurationStates().singleSignOnConfiguration());
  $stateProvider.state("gtmhub.configuration.singleSignOn._connection", stateFactory.configurationStates()._connection());
  $stateProvider.state("gtmhub.configuration.singleSignOn._connection.testConnection", stateFactory.configurationStates().testConnection());
  $stateProvider.state("gtmhub.configuration.singleSignOn._connection.mappings", stateFactory.configurationStates().mappings());
  $stateProvider.state("gtmhub.configuration.singleSignOn._connection.done", stateFactory.configurationStates().connectionDone());

  // configuration for dashboard parameters
  $stateProvider.state("gtmhub.configuration.parameters", stateFactory.configurationStates().parameters());
  $stateProvider.state("gtmhub.configuration.parameters._parameterForm", stateFactory.configurationStates()._parameterForm());
  $stateProvider.state("gtmhub.configuration.parameters._parameterForm.create", stateFactory.configurationStates().createParameter());
  $stateProvider.state("gtmhub.configuration.parameters._parameterForm.edit", stateFactory.configurationStates().editParameter());
  $stateProvider.state("gtmhub.configuration.parameters._parameterForm.done", stateFactory.configurationStates().parameterFormDone());

  // configuration for custom fields
  $stateProvider.state("gtmhub.configuration.customFields", stateFactory.configurationStates().customFields());
  $stateProvider.state("gtmhub.manageCustomFieldsOrder", stateFactory.manageCustomFieldsOrderStates().manageOrder());

  // api tokens issuing
  $stateProvider.state("gtmhub.configuration.apiTokens", stateFactory.configurationStates().apiTokens());
  $stateProvider.state("gtmhub.configuration.apiTokens._issueToken", stateFactory.configurationStates()._issueToken());
  $stateProvider.state("gtmhub.configuration.apiTokens._issueToken.issueToken", stateFactory.configurationStates().issueToken());
  $stateProvider.state("gtmhub.configuration.branding", stateFactory.configurationStates().branding());
  $stateProvider.state("gtmhub.configuration.accountNameDomainChange", stateFactory.configurationStates().accountNameOrDomainChange());

  // configuration for billing
  $stateProvider.state("gtmhub.configuration.billing", stateFactory.configurationStates().billing());
  $stateProvider.state("gtmhub.configuration.billing._portalSection", stateFactory.accountStates()._portalSection());
  $stateProvider.state("gtmhub.configuration.billing._portalSection.cancelSubscription", stateFactory.accountStates().cancelSubscription());

  // configuration for plugins
  const ___configurationStates = (state: string, config?: Ng1StateDeclaration) => {
    config && $stateProvider.state({ ...config, name: `${state}${config.name}` });
  };
  const configurationBase = "gtmhub.configuration";

  const pluginsStateMap = async(() => import(/* webpackChunkName: "plugins" */ "./plugins/plugins.module"), pluginStates);
  ___configurationStates(configurationBase, pluginsStateMap.pluginListState);
  ___configurationStates(configurationBase, pluginsStateMap.pluginSettingsState);

  $stateProvider.state("gtmhub.dispatch", stateFactory.miscStates().dispatch());

  // =======================================================================================
  // lists
  $stateProvider.state("gtmhub.lists", stateFactory.listStates().lists());
  $stateProvider.state("gtmhub.list", stateFactory.listStates().list());

  // goal lists states
  ___goalSidebarStates("gtmhub.list");

  // metric lists states
  $stateProvider.state("gtmhub.list._metric", stateFactory.goalStates()._metric());
  $stateProvider.state("gtmhub.list._metric.metric", stateFactory.goalStates().metric());
  $stateProvider.state("gtmhub.list._metric.metric._updateMetric", stateFactory.goalStates()._updateMetric());
  $stateProvider.state("gtmhub.list._metric.metric._updateMetric.update", stateFactory.goalStates().updateMetricForm());
  $stateProvider.state("gtmhub.list._metric.metric._updateMetric.edit", stateFactory.goalStates().editMetricForm());
  $stateProvider.state("gtmhub.list._metric.metric._message", stateFactory.inboxStates()._message());
  $stateProvider.state("gtmhub.list._metric.metric._message.thread", stateFactory.inboxStates().thread());

  $stateProvider.state("gtmhub.list._metric.metric._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.list._metric.metric._task.task");

  // edit dynamic metric
  $stateProvider.state("gtmhub.list._metric.metric._editMetric", stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates("gtmhub.list._metric.metric._editMetric.editDynamicMetric");

  // edit manual metric
  ______editManualMetricStates("gtmhub.list._metric.metric._editMetric.editMetric");

  // task lists states
  $stateProvider.state("gtmhub.list._task", stateFactory.taskStates()._task());
  ___________taskState("gtmhub.list._task.task");
  // Whiteboards
  register(Object.values(async(() => import(/* webpackChunkName: "whiteboards" */ "./whiteboards/whiteboards.module"), whiteboardStates)));
  ___goalSidebarStates(whiteboardStates.whiteboardState.name);

  // Insights, insightboards, data sources
  register(async(() => import(/* webpackChunkName: "insights" */ "./insights/insights.module"), insightStates));

  $stateProvider.state(`${insightboardsStateName}._form.create._ownerSelector`, stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state(`${insightboardsStateName}._form.create._ownerSelector.ownerSelector`, stateFactory.assigneeStates().ownerSelector());
  $stateProvider.state(`${insightboardsStateName}._edit.edit._ownerSelector`, stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state(`${insightboardsStateName}._edit.edit._ownerSelector.ownerSelector`, stateFactory.assigneeStates().ownerSelector());

  // _singleGoal(removed) redirect states used by marktetplace insights
  // redirect from gtmhub.insightboard._singleGoal.goal to gtmhub.insightboard._goalSidebar.goal
  $stateProvider.state(`${insightboardStateName}._singleGoal`, stateFactory.legacyRedirectStates()._singleGoal());
  $stateProvider.state(`${insightboardStateName}._singleGoal.goal`, stateFactory.legacyRedirectStates().toInsightboardGoal());
  // redirect from gtmhub.insightboard._singleGoal.goal_metric.metric to gtmhub.insightboard._goalSidebar.goal._metric.metric
  $stateProvider.state(`${insightboardStateName}._singleGoal.goal._metric`, stateFactory.legacyRedirectStates()._metric());
  $stateProvider.state(`${insightboardStateName}._singleGoal.goal._metric.metric`, stateFactory.legacyRedirectStates().toInsightboardMetric());

  // single task states are used by custom insights added to customer accounts
  $stateProvider.state(`${insightboardStateName}._task`, stateFactory.taskStates()._task());
  ___________taskState(`${insightboardStateName}._task.task`);

  $stateProvider.state(`${insightboardStateName}._metric`, stateFactory.goalStates()._metric());
  $stateProvider.state(`${insightboardStateName}._metric.metric`, stateFactory.goalStates().metric());
  $stateProvider.state(`${insightboardStateName}._metric.metric._task`, stateFactory.taskStates()._task());
  ___________taskState(`${insightboardStateName}._metric.metric._task.task`);

  // edit dynamic metric
  $stateProvider.state(`${insightboardStateName}._metric.metric._editMetric`, stateFactory.goalStates()._editMetric());
  ______editDynamicMetricStates(`${insightboardStateName}._metric.metric._editMetric.editDynamicMetric`);

  // edit manual metric
  ______editManualMetricStates(`${insightboardStateName}._metric.metric._editMetric.editMetric`);

  $stateProvider.state(`${insightboardStateName}._metric.metric._updateMetric`, stateFactory.goalStates()._updateMetric());
  $stateProvider.state(`${insightboardStateName}._metric.metric._updateMetric.update`, stateFactory.goalStates().updateMetricForm());
  $stateProvider.state(`${insightboardStateName}._metric.metric._updateMetric.edit`, stateFactory.goalStates().editMetricForm());
  $stateProvider.state(`${insightboardStateName}._metric.metric._message`, stateFactory.inboxStates()._message());
  $stateProvider.state(`${insightboardStateName}._metric.metric._message.thread`, stateFactory.inboxStates().thread());

  $stateProvider.state(`${insightboardStateName}._edit.edit._ownerSelector`, stateFactory.assigneeStates()._ownerSelector());
  $stateProvider.state(`${insightboardStateName}._edit.edit._ownerSelector.ownerSelector`, stateFactory.assigneeStates().ownerSelector());

  ___goalSidebarStates(insightboardStateName);
  $stateProvider.state(`${insightboardStateName}._closeSingleGoal`, stateFactory.goalStates()._closeSingleGoal());

  // Employees
  const employeesStateMap = async(() => import(/* webpackChunkName: "employees" */ "./employees/module"), employeesStates);
  ______employeeStates("gtmhub.employees", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.list", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.organization", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.organization_chart", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.teams.team", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.organization.team", employeesStateMap.employeeState);
  ______employeeStates("gtmhub.list.team", employeesStateMap.employeeState);

  // Teams
  const teamsStateMap = async(() => import(/* webpackChunkName: "teams" */ "./teams/module"), teamsStates);
  __________teamStates("gtmhub.teams", teamsStateMap.teamProfile);
  __________teamStates("gtmhub.list", teamsStateMap.teamProfile);
  __________teamStates("gtmhub.organization", teamsStateMap.teamProfile);
  __________teamStates("gtmhub.organization_chart", teamsStateMap.teamProfile);
  __________teamStates("gtmhub.home", teamsStateMap.teamProfile);

  // wizard state
  $stateProvider.state("gtmhub.workday", stateFactory.workdayState().workday());
  $stateProvider.state("gtmhub.workday.wizard", stateFactory.workdayState().wizard());
  $stateProvider.state("gtmhub.workday.wizard.selections", stateFactory.workdayState().wizardSelections());
  $stateProvider.state("gtmhub.workday.wizard.selections._sidebar", stateFactory.workdayState()._wizardSidebar());
  $stateProvider.state("gtmhub.workday.wizard.selections._sidebar.advanced-criteria", stateFactory.workdayState().wizardSelection());
  $stateProvider.state("gtmhub.workday.wizard.mapping", stateFactory.workdayState().wizardMapping());
  $stateProvider.state("gtmhub.workday.wizard.mapping._sidebar", stateFactory.workdayState()._wizardSidebar());
  $stateProvider.state("gtmhub.workday.wizard.mapping._sidebar.map", stateFactory.workdayState().wizardMap());
  $stateProvider.state("gtmhub.workday.wizard.provisioning", stateFactory.workdayState().wizardProvisioning());

  // configuration for workday
  $stateProvider.state("gtmhub.configuration.workday", stateFactory.configurationStates().configurationWorkday());
  $stateProvider.state("gtmhub.configuration.workday.settings", stateFactory.workdayState().workdaySettings());
  $stateProvider.state("gtmhub.configuration.workday.sync-history", stateFactory.workdayState().syncHistory());

  // configuration for scim
  $stateProvider.state("gtmhub.configuration.scim", stateFactory.configurationStates().configurationScim());

  // configuration for tags management
  $stateProvider.state("gtmhub.configuration.tags", stateFactory.configurationStates().configurationTags());
  $stateProvider.state("gtmhub.configuration.tags._createTag", stateFactory.configurationStates()._createTag());
  $stateProvider.state("gtmhub.configuration.tags._createTag.form", stateFactory.configurationStates().createTagForm());
  $stateProvider.state("gtmhub.configuration.tags._mergeTags", stateFactory.configurationStates()._mergeTags());
  $stateProvider.state("gtmhub.configuration.tags._mergeTags.form", stateFactory.configurationStates().createMergeTagsForm());

  $urlRouterProvider.otherwise(($injector) => {
    const $state = $injector.get<StateService>("$state");
    $state.go("default");
  });
}

const mod = angular.module("gh-states", [core]);

mod.config([
  "$stateProvider",
  "$urlRouterProvider",
  ($stateProvider: StateProvider, $urlRouterProvider: UrlRouterProvider) => {
    registerStates($stateProvider, $urlRouterProvider);
  },
]);

export default mod.name;
